import { useState } from 'react';
import {
  getPerfilPorEmail,
  atualizarPerfil,
  gerarTokenMock,
  type PerfilUsuario,
} from '@/mocks/backoffice/usuariosAtendimento';
import { registrarAuditoria } from '@/mocks/backoffice/auditoriaHelpers';
import { useBackofficeAuth } from '@/contexts/BackofficeAuthContext';
import {
  participantesCampanhas,
  getParticipantesPorEmail,
  atualizarStatusParticipanteCampanha,
  toggleConfigPersonalizada,
  type ParticipanteCampanha,
} from '@/mocks/backoffice/participantesCampanhas';
import {
  campanhasBackoffice,
  getStatusColor as getCampanhaStatusColor,
  getStatusLabel as getCampanhaStatusLabel,
  getTipoLabel,
  getTipoColor,
  type CampanhaBackoffice,
} from '@/mocks/backoffice/campanhasBackoffice';
import {
  getConfigParticipanteCampanha,
  salvarConfigParticipanteCampanha,
  removerConfigParticipanteCampanha,
  type ParticipanteCampanhaConfig,
} from '@/mocks/backoffice/participanteCampanhaConfig';
import {
  getAtividadesPorEmail,
  getResumoAtividades,
  getIconAtividade,
  getColorAtividade,
  getBgColorAtividade,
  getLabelAtividade,
  getStatusColorAtividade,
  type TipoAtividade,
  type AtividadeUsuario,
} from '@/mocks/backoffice/atividadesUsuario';

interface UserProfilePanelProps {
  usuarioEmail: string;
  usuarioNome: string;
  usuarioTipo: 'empresa' | 'participante';
}

const statusConfig: Record<string, { bg: string; text: string; label: string }> = {
  ativo: { bg: 'bg-emerald-50', text: 'text-emerald-600', label: 'Ativo' },
  inativo: { bg: 'bg-gray-50', text: 'text-gray-500', label: 'Inativo' },
  pendente: { bg: 'bg-amber-50', text: 'text-amber-600', label: 'Pendente' },
  bloqueado: { bg: 'bg-red-50', text: 'text-red-600', label: 'Bloqueado' },
};

function tempoRelativo(dataStr: string): string {
  const diff = Date.now() - new Date(dataStr).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'Agora';
  if (min < 60) return `${min}min atrás`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h atrás`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d atrás`;
  return new Date(dataStr).toLocaleDateString('pt-BR');
}

function dataExata(dataStr: string): string {
  return new Date(dataStr).toLocaleString('pt-BR');
}

export default function UserProfilePanel({ usuarioEmail, usuarioNome, usuarioTipo }: UserProfilePanelProps) {
  const { session } = useBackofficeAuth();
  const [perfil, setPerfil] = useState<PerfilUsuario | undefined>(getPerfilPorEmail(usuarioEmail));
  const [editando, setEditando] = useState(false);
  const [editData, setEditData] = useState<Partial<PerfilUsuario>>();
  const [toast, setToast] = useState('');
  const [tabAtiva, setTabAtiva] = useState<'info' | 'endereco' | 'historico' | 'acoes' | 'campanhas'>('info');
  const [canalToken, setCanalToken] = useState<'email' | 'sms' | 'whatsapp'>('email');
  const [enviandoToken, setEnviandoToken] = useState(false);
  const [enviandoReset, setEnviandoReset] = useState(false);
  const [campanhaEditando, setCampanhaEditando] = useState<ParticipanteCampanha | null>(null);
  const [configEditData, setConfigEditData] = useState<Partial<ParticipanteCampanhaConfig>>();
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [confirmModal, setConfirmModal] = useState<{ tipo: string; titulo: string; mensagem: string; acao: () => void } | null>(null);
  const [filtroAtividade, setFiltroAtividade] = useState<TipoAtividade | 'todos'>('todos');

  const usuarioNomeAuth = session.nome || 'Usuário';
  const usuarioRoleAuth = session.role || 'operador';

  const mostrarToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  const iniciarEdicao = () => {
    if (!perfil) return;
    setEditData({
      nome: perfil.nome,
      email: perfil.email,
      telefone: perfil.telefone,
      cpf: perfil.cpf,
      status: perfil.status,
      contatoAlternativo: perfil.contatoAlternativo,
      observacoes: perfil.observacoes,
      endereco: { ...perfil.endereco },
    });
    setEditando(true);
  };

  const salvarEdicao = () => {
    if (!perfil || !editData) return;
    const alteracoes: string[] = [];
    if (editData.nome && editData.nome !== perfil.nome) alteracoes.push(`nome: "${perfil.nome}" → "${editData.nome}"`);
    if (editData.email && editData.email !== perfil.email) alteracoes.push(`email: "${perfil.email}" → "${editData.email}"`);
    if (editData.telefone && editData.telefone !== perfil.telefone) alteracoes.push(`telefone: "${perfil.telefone}" → "${editData.telefone}"`);
    if (editData.status && editData.status !== perfil.status) alteracoes.push(`status: "${perfil.status}" → "${editData.status}"`);
    if (editData.contatoAlternativo && editData.contatoAlternativo !== perfil.contatoAlternativo) alteracoes.push('contato alternativo alterado');

    const atualizado = atualizarPerfil(perfil.id, editData);
    if (atualizado) {
      setPerfil(atualizado);
      setEditando(false);
      mostrarToast('Perfil atualizado com sucesso!');
      if (alteracoes.length > 0) {
        registrarAuditoria('editar_perfil_usuario', 'atendimento', `Editou perfil do usuário ${perfil.nome} (${perfil.id}) — ${alteracoes.join('; ')}`, usuarioNomeAuth, usuarioRoleAuth, perfil.id, 'usuario');
      }
    }
  };

  const cancelarEdicao = () => {
    setEditando(false);
    setEditData({});
  };

  const handleEnderecoChange = (campo: string, valor: string) => {
    setEditData((prev) => ({
      ...prev,
      endereco: { ...(prev?.endereco || {}), [campo]: valor } as PerfilUsuario['endereco'],
    }));
  };

  // ─── Ações de Suporte ───

  const handleResetSenha = () => {
    if (!perfil) return;
    setEnviandoReset(true);
    setTimeout(() => {
      const codigo = gerarTokenMock('email');
      const novoHistorico = [
        ...(perfil.historicoTokens || []),
        { tipo: 'reset_senha' as const, canal: 'email' as const, enviadoEm: new Date().toISOString(), expiraEm: new Date(Date.now() + 30 * 60000).toISOString(), status: 'pendente' as const, codigoMascarado: `***${codigo.slice(-3)}` },
      ];
      const atualizado = atualizarPerfil(perfil.id, {
        tokenResetPendente: true,
        tokenResetEnviadoEm: new Date().toISOString(),
        tokenResetCanal: 'email',
        historicoTokens: novoHistorico,
        ultimaAlteracaoSenha: undefined,
      });
      if (atualizado) setPerfil(atualizado);
      registrarAuditoria('reset_senha_usuario', 'atendimento', `Enviou link de reset de senha para ${perfil.nome} (${perfil.id}) via email. Código: ***${codigo.slice(-3)}`, usuarioNomeAuth, usuarioRoleAuth, perfil.id, 'usuario');
      mostrarToast(`Email de reset enviado para ${perfil.email}`);
      setEnviandoReset(false);
    }, 1200);
  };

  const handleEnviarToken = () => {
    if (!perfil) return;
    setEnviandoToken(true);
    setTimeout(() => {
      const codigo = gerarTokenMock(canalToken);
      const novoHistorico = [
        ...(perfil.historicoTokens || []),
        { tipo: 'verificacao' as const, canal: canalToken, enviadoEm: new Date().toISOString(), expiraEm: new Date(Date.now() + 10 * 60000).toISOString(), status: 'pendente' as const, codigoMascarado: `***${codigo.slice(-3)}` },
      ];
      const atualizado = atualizarPerfil(perfil.id, { historicoTokens: novoHistorico });
      if (atualizado) setPerfil(atualizado);
      const canalLabel = canalToken === 'email' ? 'email' : canalToken === 'sms' ? 'SMS' : 'WhatsApp';
      registrarAuditoria('enviar_token_usuario', 'atendimento', `Enviou token de verificação para ${perfil.nome} via ${canalLabel}. Código: ***${codigo.slice(-3)}`, usuarioNomeAuth, usuarioRoleAuth, perfil.id, 'usuario');
      mostrarToast(`Token enviado via ${canalLabel} para ${canalToken === 'email' ? perfil.email : perfil.telefone}`);
      setEnviandoToken(false);
    }, 1200);
  };

  const handleBloquearConta = () => {
    if (!perfil) return;
    const novoStatus = perfil.status === 'bloqueado' ? 'ativo' : 'bloqueado';
    const acaoLabel = novoStatus === 'bloqueado' ? 'bloqueou' : 'desbloqueou';
    setConfirmModal({
      tipo: 'bloquear',
      titulo: novoStatus === 'bloqueado' ? 'Bloquear conta' : 'Desbloquear conta',
      mensagem: novoStatus === 'bloqueado'
        ? `Tem certeza que deseja bloquear a conta de ${perfil.nome}? O usuário não conseguirá fazer login.`
        : `Desbloquear a conta de ${perfil.nome}? O usuário poderá fazer login novamente.`,
      acao: () => {
        const atualizado = atualizarPerfil(perfil.id, { status: novoStatus });
        if (atualizado) setPerfil(atualizado);
        registrarAuditoria(`${acaoLabel}_conta_usuario`, 'atendimento', `${acaoLabel.charAt(0).toUpperCase() + acaoLabel.slice(1)} a conta de ${perfil.nome} (${perfil.id})`, usuarioNomeAuth, usuarioRoleAuth, perfil.id, 'usuario');
        mostrarToast(`Conta ${novoStatus === 'bloqueado' ? 'bloqueada' : 'desbloqueada'} com sucesso!`);
        setConfirmModal(null);
      },
    });
  };

  const handleToggleMFA = () => {
    if (!perfil) return;
    const novoMFA = !perfil.mfaHabilitado;
    setConfirmModal({
      tipo: 'mfa',
      titulo: novoMFA ? 'Habilitar 2FA' : 'Desabilitar 2FA',
      mensagem: novoMFA
        ? `Habilitar autenticação de dois fatores para ${perfil.nome}?`
        : `Desabilitar autenticação de dois fatores para ${perfil.nome}? Isso reduz a segurança da conta.`,
      acao: () => {
        const atualizado = atualizarPerfil(perfil.id, { mfaHabilitado: novoMFA });
        if (atualizado) setPerfil(atualizado);
        registrarAuditoria(`${novoMFA ? 'habilitar' : 'desabilitar'}_mfa`, 'atendimento', `${novoMFA ? 'Habilitou' : 'Desabilitou'} 2FA para ${perfil.nome} (${perfil.id})`, usuarioNomeAuth, usuarioRoleAuth, perfil.id, 'usuario');
        mostrarToast(`2FA ${novoMFA ? 'habilitado' : 'desabilitado'} com sucesso!`);
        setConfirmModal(null);
      },
    });
  };

  const handleEncerrarSessoes = () => {
    if (!perfil) return;
    setConfirmModal({
      tipo: 'sessoes',
      titulo: 'Encerrar todas as sessões',
      mensagem: `Encerrar todas as sessões ativas de ${perfil.nome}? O usuário será deslogado de todos os dispositivos e precisará fazer login novamente.`,
      acao: () => {
        const atualizado = atualizarPerfil(perfil.id, { dispositivosAtivos: 0 });
        if (atualizado) setPerfil(atualizado);
        registrarAuditoria('encerrar_sessoes_usuario', 'atendimento', `Encerrou todas as sessões de ${perfil.nome} (${perfil.id})`, usuarioNomeAuth, usuarioRoleAuth, perfil.id, 'usuario');
        mostrarToast('Todas as sessões encerradas!');
        setConfirmModal(null);
      },
    });
  };

  const handleVerificarContato = (tipo: 'email' | 'telefone') => {
    if (!perfil) return;
    const destino = tipo === 'email' ? perfil.email : perfil.telefone;
    registrarAuditoria('verificar_contato_usuario', 'atendimento', `Verificou ${tipo === 'email' ? 'email' : 'telefone'} de ${perfil.nome}: ${destino}`, usuarioNomeAuth, usuarioRoleAuth, perfil.id, 'usuario');
    mostrarToast(`Ping de verificação enviado para ${tipo === 'email' ? 'email' : 'celular'}!`);
  };

  const handleCancelarTokenReset = () => {
    if (!perfil) return;
    const atualizado = atualizarPerfil(perfil.id, {
      tokenResetPendente: false,
      tokenResetEnviadoEm: undefined,
      tokenResetCanal: undefined,
    });
    if (atualizado) setPerfil(atualizado);
    registrarAuditoria('cancelar_token_reset', 'atendimento', `Cancelou token de reset de senha pendente de ${perfil.nome} (${perfil.id})`, usuarioNomeAuth, usuarioRoleAuth, perfil.id, 'usuario');
    mostrarToast('Token de reset cancelado.');
  };

  // ─── Ações de Campanha ───

  const participantesDoUsuario = perfil ? getParticipantesPorEmail(perfil.email) : [];

  const getCampanhaInfo = (campanhaId: string): CampanhaBackoffice | undefined => {
    return campanhasBackoffice.find((c) => c.id === campanhaId);
  };

  const handleAlterarStatusCampanha = (participante: ParticipanteCampanha, novoStatus: ParticipanteCampanha['status']) => {
    const campanha = getCampanhaInfo(participante.campanhaId);
    const atualizado = atualizarStatusParticipanteCampanha(participante.id, novoStatus);
    if (atualizado) {
      registrarAuditoria('alterar_status_participante_campanha', 'atendimento', `Alterou status de ${perfil?.nome || participante.nome} na campanha ${campanha?.nome || participante.campanhaId} de ${participante.status} para ${novoStatus}`, usuarioNomeAuth, usuarioRoleAuth, participante.id, 'participante_campanha');
      mostrarToast(`Status na campanha ${campanha?.nome || participante.campanhaId} alterado para ${novoStatus}`);
    }
  };

  const handleToggleConfigPersonalizada = (participante: ParticipanteCampanha) => {
    const campanha = getCampanhaInfo(participante.campanhaId);
    const novoValor = !participante.configPersonalizada;
    const atualizado = toggleConfigPersonalizada(participante.id, novoValor);
    if (atualizado) {
      if (novoValor) {
        // Ativa config - cria registro padrão
        salvarConfigParticipanteCampanha({
          participanteId: participante.id,
          campanhaId: participante.campanhaId,
          empresaId: participante.empresaId,
          ativo: true,
          alteradoPor: usuarioNomeAuth,
        });
      } else {
        // Desativa config - remove
        removerConfigParticipanteCampanha(participante.id, participante.campanhaId);
      }
      registrarAuditoria(`${novoValor ? 'ativar' : 'desativar'}_config_personalizada`, 'atendimento', `${novoValor ? 'Ativou' : 'Desativou'} configuração personalizada de ${perfil?.nome || participante.nome} na campanha ${campanha?.nome || participante.campanhaId}`, usuarioNomeAuth, usuarioRoleAuth, participante.id, 'participante_campanha');
      mostrarToast(`Configuração personalizada ${novoValor ? 'ativada' : 'desativada'} na campanha ${campanha?.nome || participante.campanhaId}`);
    }
  };

  const handleAbrirConfigModal = (participante: ParticipanteCampanha) => {
    const config = getConfigParticipanteCampanha(participante.id, participante.campanhaId);
    setConfigEditData({
      limiteResgateDiarioPersonalizado: config?.limiteResgateDiarioPersonalizado,
      limiteResgateMensalPersonalizado: config?.limiteResgateMensalPersonalizado,
      taxaProcessamentoPixPersonalizada: config?.taxaProcessamentoPixPersonalizada,
      prazoAprovacaoSaquePersonalizado: config?.prazoAprovacaoSaquePersonalizado,
    });
    setCampanhaEditando(participante);
    setShowConfigModal(true);
  };

  const handleSalvarConfig = () => {
    if (!campanhaEditando || !configEditData) return;
    const campanha = getCampanhaInfo(campanhaEditando.campanhaId);
    salvarConfigParticipanteCampanha({
      participanteId: campanhaEditando.id,
      campanhaId: campanhaEditando.campanhaId,
      empresaId: campanhaEditando.empresaId,
      ...configEditData,
      ativo: true,
      alteradoPor: usuarioNomeAuth,
    });
    toggleConfigPersonalizada(campanhaEditando.id, true);
    registrarAuditoria('editar_config_participante_campanha', 'atendimento', `Editou configuração personalizada de ${perfil?.nome || campanhaEditando.nome} na campanha ${campanha?.nome || campanhaEditando.campanhaId}`, usuarioNomeAuth, usuarioRoleAuth, campanhaEditando.id, 'participante_campanha');
    mostrarToast('Configuração personalizada salva!');
    setShowConfigModal(false);
    setCampanhaEditando(null);
  };

  const handleRemoverParticipanteCampanha = (participante: ParticipanteCampanha) => {
    const campanha = getCampanhaInfo(participante.campanhaId);
    setConfirmModal({
      tipo: 'remover_campanha',
      titulo: 'Remover da Campanha',
      mensagem: `Tem certeza que deseja remover ${perfil?.nome || participante.nome} da campanha "${campanha?.nome || participante.campanhaId}"? O participante perderá o acesso e os pontos acumulados nesta campanha.`,
      acao: () => {
        const idx = participantesCampanhas.findIndex((p) => p.id === participante.id);
        if (idx >= 0) {
          participantesCampanhas.splice(idx, 1);
          removerConfigParticipanteCampanha(participante.id, participante.campanhaId);
          registrarAuditoria('remover_participante_campanha', 'atendimento', `Removeu ${perfil?.nome || participante.nome} da campanha ${campanha?.nome || participante.campanhaId}`, usuarioNomeAuth, usuarioRoleAuth, participante.id, 'participante_campanha');
          mostrarToast('Participante removido da campanha');
        }
        setConfirmModal(null);
      },
    });
  };

  const tempoDesdeCadastro = perfil
    ? Math.floor((Date.now() - new Date(perfil.dataCadastro).getTime()) / (1000 * 60 * 60 * 24 * 30))
    : 0;

  const diasDesdeUltimaSenha = perfil?.ultimaAlteracaoSenha
    ? Math.floor((Date.now() - new Date(perfil.ultimaAlteracaoSenha).getTime()) / (1000 * 60 * 60 * 24))
    : null;

  const tabs = [
    { id: 'campanhas' as const, label: 'Campanhas', icon: 'ri-trophy-line' },
    { id: 'info' as const, label: 'Dados', icon: 'ri-user-line' },
    { id: 'endereco' as const, label: 'Endereço', icon: 'ri-map-pin-line' },
    { id: 'historico' as const, label: 'Histórico', icon: 'ri-time-line' },
    { id: 'acoes' as const, label: 'Ações', icon: 'ri-shield-keyhole-line' },
  ];

  return (
    <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
      {/* Toast */}
      {toast && (
        <div className="px-4 py-2 bg-emerald-50 border-b border-emerald-100 flex items-center space-x-2">
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-checkbox-circle-line text-emerald-600 text-sm"></i>
          </div>
          <span className="text-xs font-medium text-emerald-700">{toast}</span>
        </div>
      )}

      {/* Header */}
      <div className="px-4 py-4 border-b border-gray-100">
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-full bg-stone-700 flex items-center justify-center">
              <span className="text-sm font-bold text-white">{usuarioNome.charAt(0)}</span>
            </div>
            <div>
              <h3 className="text-sm font-bold text-gray-800">{usuarioNome}</h3>
              <div className="flex items-center space-x-2 mt-0.5">
                <span className="text-[10px] text-gray-400 capitalize">
                  {usuarioTipo === 'empresa' ? 'Responsável Empresa' : 'Participante'}
                </span>
                {perfil && (
                  <span className={`inline-flex px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${statusConfig[perfil.status]?.bg || statusConfig.ativo.bg} ${statusConfig[perfil.status]?.text || statusConfig.ativo.text}`}>
                    {statusConfig[perfil.status]?.label || perfil.status}
                  </span>
                )}
              </div>
            </div>
          </div>
          {perfil && !editando && (
            <button onClick={iniciarEdicao} className="p-1.5 hover:bg-gray-100 rounded-lg transition-all cursor-pointer" title="Editar perfil">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-pencil-line text-gray-400 text-sm"></i>
              </div>
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-100">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setTabAtiva(tab.id)}
            className={`flex-1 flex items-center justify-center space-x-1.5 py-2.5 text-xs font-medium transition-all cursor-pointer ${
              tabAtiva === tab.id ? 'text-emerald-600 border-b-2 border-emerald-500' : 'text-gray-400 hover:text-gray-600'
            }`}
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className={tab.icon}></i>
            </div>
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="p-4">
        {!perfil ? (
          <div className="text-center py-6">
            <div className="w-10 h-10 rounded-full bg-gray-50 flex items-center justify-center mx-auto mb-2">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-user-search-line text-gray-300 text-lg"></i>
              </div>
            </div>
            <p className="text-xs text-gray-400">Perfil não disponível</p>
            <p className="text-[10px] text-gray-300 mt-1">{usuarioEmail}</p>
          </div>
        ) : (
          <>
            {/* ─── CAMPANHAS TAB ─── */}
            {tabAtiva === 'campanhas' && (
              <div className="space-y-3">
                {participantesDoUsuario.length === 0 ? (
                  <div className="text-center py-6">
                    <div className="w-10 h-10 rounded-full bg-gray-50 flex items-center justify-center mx-auto mb-2">
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-trophy-line text-gray-300 text-lg"></i>
                      </div>
                    </div>
                    <p className="text-xs text-gray-400">Nenhuma campanha vinculada</p>
                    <p className="text-[10px] text-gray-300 mt-1">Este usuário não participa de campanhas ativas</p>
                  </div>
                ) : (
                  <>
                    {/* Cards de Resumo */}
                    <div className="grid grid-cols-2 gap-2">
                      <div className="bg-emerald-50 rounded-lg p-2.5 text-center">
                        <p className="text-xs font-bold text-gray-800">{participantesDoUsuario.filter(p => p.status === 'ativo').length}</p>
                        <p className="text-[9px] text-gray-400">Campanhas ativas</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-2.5 text-center">
                        <p className="text-xs font-bold text-gray-800">{participantesDoUsuario.reduce((acc, p) => acc + p.pontosDisponiveis, 0).toLocaleString('pt-BR')}</p>
                        <p className="text-[9px] text-gray-400">Pontos disponíveis</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-2.5 text-center">
                        <p className="text-xs font-bold text-gray-800">{participantesDoUsuario.reduce((acc, p) => acc + p.pontosAcumulados, 0).toLocaleString('pt-BR')}</p>
                        <p className="text-[9px] text-gray-400">Pontos acumulados</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-2.5 text-center">
                        <p className="text-xs font-bold text-gray-800">{participantesDoUsuario.filter(p => p.configPersonalizada).length}</p>
                        <p className="text-[9px] text-gray-400">Configs personalizadas</p>
                      </div>
                    </div>

                    {/* Lista de Campanhas */}
                    <div className="space-y-2">
                      {participantesDoUsuario.map((participante) => {
                        const campanha = getCampanhaInfo(participante.campanhaId);
                        const cfg = getConfigParticipanteCampanha(participante.id, participante.campanhaId);
                        const statusCfg = statusConfig[participante.status] || statusConfig.ativo;
                        return (
                          <div key={participante.id} className="bg-gray-50 rounded-lg p-3 space-y-2">
                            {/* Header do card */}
                            <div className="flex items-start justify-between">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center space-x-1.5">
                                  <p className="text-xs font-bold text-gray-800 truncate">{campanha?.nome || participante.campanhaId}</p>
                                  {campanha && (
                                    <span className={`inline-flex px-1.5 py-0.5 rounded text-[9px] font-bold ${getTipoColor(campanha.tipo)}`}>
                                      {getTipoLabel(campanha.tipo)}
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center space-x-1.5 mt-0.5">
                                  <span className={`inline-flex px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${statusCfg.bg} ${statusCfg.text}`}>
                                    {statusCfg.label || participante.status}
                                  </span>
                                  {participante.configPersonalizada && (
                                    <span className="inline-flex items-center space-x-0.5 px-1.5 py-0.5 rounded text-[9px] font-bold bg-amber-50 text-amber-600">
                                      <div className="w-3 h-3 flex items-center justify-center">
                                        <i className="ri-settings-3-line text-[8px]"></i>
                                      </div>
                                      <span>Personalizado</span>
                                    </span>
                                  )}
                                  {cfg?.ativo === false && (
                                    <span className="inline-flex px-1.5 py-0.5 rounded text-[9px] font-bold bg-gray-100 text-gray-500">
                                      Config inativa
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Stats */}
                            <div className="grid grid-cols-3 gap-2">
                              <div className="text-center">
                                <p className="text-[10px] text-gray-400">Pontos</p>
                                <p className="text-xs font-bold text-gray-800">{participante.pontosDisponiveis.toLocaleString('pt-BR')}</p>
                              </div>
                              <div className="text-center">
                                <p className="text-[10px] text-gray-400">Acumulados</p>
                                <p className="text-xs font-bold text-gray-800">{participante.pontosAcumulados.toLocaleString('pt-BR')}</p>
                              </div>
                              <div className="text-center">
                                <p className="text-[10px] text-gray-400">Nível</p>
                                <p className="text-xs font-bold text-gray-800">{participante.nivel}</p>
                              </div>
                            </div>

                            {/* Config personalizada preview */}
                            {participante.configPersonalizada && cfg && cfg.ativo && (
                              <div className="bg-white rounded-md p-2 space-y-1">
                                <p className="text-[9px] font-bold text-gray-400 uppercase tracking-wider">Config. Personalizada</p>
                                <div className="grid grid-cols-2 gap-1">
                                  {cfg.limiteResgateDiarioPersonalizado !== undefined && (
                                    <p className="text-[10px] text-gray-600"><span className="text-gray-400">Limite diário:</span> R$ {cfg.limiteResgateDiarioPersonalizado.toLocaleString('pt-BR')}</p>
                                  )}
                                  {cfg.limiteResgateMensalPersonalizado !== undefined && (
                                    <p className="text-[10px] text-gray-600"><span className="text-gray-400">Limite mensal:</span> R$ {cfg.limiteResgateMensalPersonalizado.toLocaleString('pt-BR')}</p>
                                  )}
                                  {cfg.taxaProcessamentoPixPersonalizada !== undefined && (
                                    <p className="text-[10px] text-gray-600"><span className="text-gray-400">Taxa PIX:</span> {cfg.taxaProcessamentoPixPersonalizada}%</p>
                                  )}
                                  {cfg.prazoAprovacaoSaquePersonalizado !== undefined && (
                                    <p className="text-[10px] text-gray-600"><span className="text-gray-400">Prazo aprovação:</span> {cfg.prazoAprovacaoSaquePersonalizado}h</p>
                                  )}
                                </div>
                                <p className="text-[9px] text-gray-400 mt-1">Alterado por {cfg.alteradoPor} · {tempoRelativo(cfg.ultimaAlteracao)}</p>
                              </div>
                            )}

                            {/* Actions */}
                            <div className="flex items-center space-x-1 pt-1">
                              {/* Alterar Status */}
                              <div className="relative group">
                                <button className="px-2 py-1 bg-white hover:bg-gray-100 border border-gray-200 rounded-md text-[10px] font-bold text-gray-600 transition-all cursor-pointer flex items-center space-x-1">
                                  <div className="w-3 h-3 flex items-center justify-center">
                                    <i className="ri-refresh-line text-[10px]"></i>
                                  </div>
                                  <span>Status</span>
                                </button>
                                <div className="absolute left-0 top-full mt-1 bg-white border border-gray-100 rounded-lg shadow-lg z-10 hidden group-hover:block min-w-[120px]">
                                  {(['ativo', 'pendente', 'bloqueado'] as const).map((s) => (
                                    <button
                                      key={s}
                                      onClick={() => handleAlterarStatusCampanha(participante, s)}
                                      className={`w-full text-left px-3 py-1.5 text-[10px] font-medium hover:bg-gray-50 transition-colors cursor-pointer first:rounded-t-lg last:rounded-b-lg ${participante.status === s ? 'text-emerald-600 bg-emerald-50' : 'text-gray-700'}`}
                                    >
                                      <span className="capitalize">{s}</span>
                                      {participante.status === s && <i className="ri-check-line ml-1 text-emerald-500"></i>}
                                    </button>
                                  ))}
                                </div>
                              </div>

                              {/* Toggle Config */}
                              <button
                                onClick={() => handleToggleConfigPersonalizada(participante)}
                                className={`px-2 py-1 rounded-md text-[10px] font-bold transition-all cursor-pointer flex items-center space-x-1 ${
                                  participante.configPersonalizada
                                    ? 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                                    : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-100'
                                }`}
                              >
                                <div className="w-3 h-3 flex items-center justify-center">
                                  <i className={`${participante.configPersonalizada ? 'ri-settings-3-fill' : 'ri-settings-3-line'} text-[10px]`}></i>
                                </div>
                                <span>{participante.configPersonalizada ? 'Personalizado' : 'Personalizar'}</span>
                              </button>

                              {/* Editar Config */}
                              {participante.configPersonalizada && (
                                <button
                                  onClick={() => handleAbrirConfigModal(participante)}
                                  className="px-2 py-1 bg-white hover:bg-gray-100 border border-gray-200 rounded-md text-[10px] font-bold text-gray-600 transition-all cursor-pointer flex items-center space-x-1"
                                >
                                  <div className="w-3 h-3 flex items-center justify-center">
                                    <i className="ri-pencil-line text-[10px]"></i>
                                  </div>
                                  <span>Editar</span>
                                </button>
                              )}

                              {/* Remover */}
                              <button
                                onClick={() => handleRemoverParticipanteCampanha(participante)}
                                className="px-2 py-1 bg-white hover:bg-red-50 border border-gray-200 hover:border-red-200 rounded-md text-[10px] font-bold text-gray-500 hover:text-red-600 transition-all cursor-pointer flex items-center space-x-1"
                              >
                                <div className="w-3 h-3 flex items-center justify-center">
                                  <i className="ri-close-line text-[10px]"></i>
                                </div>
                                <span>Remover</span>
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* ─── INFO TAB ─── */}
            {tabAtiva === 'info' && (
              <div className="space-y-3">
                {editando ? (
                  <>
                    <div>
                      <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Nome</label>
                      <input type="text" value={editData?.nome || ''} onChange={(e) => setEditData({ ...editData, nome: e.target.value })} className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500" />
                    </div>
                    <div>
                      <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Email</label>
                      <input type="email" value={editData?.email || ''} onChange={(e) => setEditData({ ...editData, email: e.target.value })} className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Telefone</label>
                        <input type="text" value={editData?.telefone || ''} onChange={(e) => setEditData({ ...editData, telefone: e.target.value })} className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500" />
                      </div>
                      <div>
                        <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">CPF</label>
                        <input type="text" value={editData?.cpf || ''} onChange={(e) => setEditData({ ...editData, cpf: e.target.value })} className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500" />
                      </div>
                    </div>
                    <div>
                      <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Status da Conta</label>
                      <select value={editData?.status || perfil.status} onChange={(e) => setEditData({ ...editData, status: e.target.value as PerfilUsuario['status'] })} className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 cursor-pointer">
                        <option value="ativo">Ativo</option>
                        <option value="inativo">Inativo</option>
                        <option value="pendente">Pendente</option>
                        <option value="bloqueado">Bloqueado</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Contato Alternativo</label>
                      <input type="text" value={editData?.contatoAlternativo || ''} onChange={(e) => setEditData({ ...editData, contatoAlternativo: e.target.value })} className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500" placeholder="Telefone alternativo" />
                    </div>
                    <div>
                      <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Observações</label>
                      <textarea value={editData?.observacoes || ''} onChange={(e) => setEditData({ ...editData, observacoes: e.target.value })} rows={2} className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 resize-none" />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <p className="text-[10px] text-gray-400">ID</p>
                        <p className="text-xs font-medium text-gray-700 font-mono">{perfil.id}</p>
                      </div>
                      <div>
                        <p className="text-[10px] text-gray-400">Cadastro</p>
                        <p className="text-xs text-gray-700">{new Date(perfil.dataCadastro).toLocaleDateString('pt-BR')} · {tempoDesdeCadastro} meses</p>
                      </div>
                    </div>
                    <div>
                      <p className="text-[10px] text-gray-400">Email</p>
                      <p className="text-xs text-gray-700">{perfil.email}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <p className="text-[10px] text-gray-400">Telefone</p>
                        <p className="text-xs text-gray-700">{perfil.telefone}</p>
                      </div>
                      <div>
                        <p className="text-[10px] text-gray-400">CPF</p>
                        <p className="text-xs text-gray-700">{perfil.cpf}</p>
                      </div>
                    </div>
                    <div>
                      <p className="text-[10px] text-gray-400">Nascimento</p>
                      <p className="text-xs text-gray-700">{new Date(perfil.dataNascimento).toLocaleDateString('pt-BR')}</p>
                    </div>
                    {perfil.empresaNome && (
                      <div>
                        <p className="text-[10px] text-gray-400">Empresa</p>
                        <p className="text-xs font-medium text-gray-700">{perfil.empresaNome}</p>
                      </div>
                    )}
                    <div>
                      <p className="text-[10px] text-gray-400">Último acesso</p>
                      <p className="text-xs text-gray-700">{new Date(perfil.ultimoAcesso).toLocaleString('pt-BR')}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <p className="text-[10px] text-gray-400">Dispositivos ativos</p>
                        <p className="text-xs text-gray-700">{perfil.dispositivosAtivos ?? 1} {perfil.dispositivosAtivos && perfil.dispositivosAtivos > 1 ? 'dispositivos' : 'dispositivo'}</p>
                      </div>
                      <div>
                        <p className="text-[10px] text-gray-400">2FA</p>
                        <p className="text-xs text-gray-700">{perfil.mfaHabilitado ? 'Habilitado' : 'Desabilitado'}</p>
                      </div>
                    </div>
                    {perfil.ipUltimoAcesso && (
                      <div>
                        <p className="text-[10px] text-gray-400">IP último acesso</p>
                        <p className="text-xs text-gray-700 font-mono">{perfil.ipUltimoAcesso}</p>
                      </div>
                    )}
                    {diasDesdeUltimaSenha !== null && (
                      <div>
                        <p className="text-[10px] text-gray-400">Última troca de senha</p>
                        <p className="text-xs text-gray-700">{diasDesdeUltimaSenha} dias atrás</p>
                      </div>
                    )}
                    {perfil.tentativasLoginFalhas && perfil.tentativasLoginFalhas > 0 && (
                      <div className="bg-red-50 rounded-lg p-2.5">
                        <div className="flex items-center space-x-1.5">
                          <div className="w-3 h-3 flex items-center justify-center">
                            <i className="ri-error-warning-line text-red-500 text-xs"></i>
                          </div>
                          <p className="text-[10px] font-bold text-red-600">{perfil.tentativasLoginFalhas} tentativas de login falhas</p>
                        </div>
                        {perfil.ultimaTentativaLogin && (
                          <p className="text-[9px] text-red-400 mt-0.5">Última: {tempoRelativo(perfil.ultimaTentativaLogin)}</p>
                        )}
                      </div>
                    )}
                    {perfil.tokenResetPendente && (
                      <div className="bg-amber-50 rounded-lg p-2.5">
                        <div className="flex items-center space-x-1.5">
                          <div className="w-3 h-3 flex items-center justify-center">
                            <i className="ri-mail-send-line text-amber-500 text-xs"></i>
                          </div>
                          <p className="text-[10px] font-bold text-amber-600">Token de reset pendente</p>
                        </div>
                        <p className="text-[9px] text-amber-500 mt-0.5">
                          Via {perfil.tokenResetCanal === 'email' ? 'email' : perfil.tokenResetCanal === 'sms' ? 'SMS' : 'WhatsApp'} · {perfil.tokenResetEnviadoEm ? tempoRelativo(perfil.tokenResetEnviadoEm) : ''}
                        </p>
                      </div>
                    )}
                    {perfil.contatoAlternativo && (
                      <div>
                        <p className="text-[10px] text-gray-400">Contato alternativo</p>
                        <p className="text-xs text-gray-700">{perfil.contatoAlternativo}</p>
                      </div>
                    )}
                    {perfil.observacoes && (
                      <div className="bg-gray-50 rounded-lg p-2.5">
                        <p className="text-[10px] text-gray-400 mb-1">Observações internas</p>
                        <p className="text-xs text-gray-600 italic">"{perfil.observacoes}"</p>
                      </div>
                    )}
                  </>
                )}

                {editando && (
                  <div className="flex items-center space-x-2 pt-2">
                    <button onClick={salvarEdicao} className="flex-1 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-lg transition-all cursor-pointer flex items-center justify-center space-x-1">
                      <i className="ri-check-line"></i>
                      <span>Salvar</span>
                    </button>
                    <button onClick={cancelarEdicao} className="flex-1 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-bold rounded-lg transition-all cursor-pointer flex items-center justify-center space-x-1">
                      <i className="ri-close-line"></i>
                      <span>Cancelar</span>
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* ─── ENDEREÇO TAB ─── */}
            {tabAtiva === 'endereco' && (
              <div className="space-y-3">
                {editando ? (
                  <>
                    <div>
                      <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Logradouro</label>
                      <input type="text" value={editData?.endereco?.logradouro || perfil.endereco.logradouro} onChange={(e) => handleEnderecoChange('logradouro', e.target.value)} className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Número</label>
                        <input type="text" value={editData?.endereco?.numero || perfil.endereco.numero} onChange={(e) => handleEnderecoChange('numero', e.target.value)} className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500" />
                      </div>
                      <div>
                        <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Complemento</label>
                        <input type="text" value={editData?.endereco?.complemento || perfil.endereco.complemento || ''} onChange={(e) => handleEnderecoChange('complemento', e.target.value)} className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500" />
                      </div>
                    </div>
                    <div>
                      <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Bairro</label>
                      <input type="text" value={editData?.endereco?.bairro || perfil.endereco.bairro} onChange={(e) => handleEnderecoChange('bairro', e.target.value)} className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Cidade</label>
                        <input type="text" value={editData?.endereco?.cidade || perfil.endereco.cidade} onChange={(e) => handleEnderecoChange('cidade', e.target.value)} className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500" />
                      </div>
                      <div>
                        <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Estado</label>
                        <input type="text" value={editData?.endereco?.estado || perfil.endereco.estado} onChange={(e) => handleEnderecoChange('estado', e.target.value)} className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500" />
                      </div>
                    </div>
                    <div>
                      <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">CEP</label>
                      <input type="text" value={editData?.endereco?.cep || perfil.endereco.cep} onChange={(e) => handleEnderecoChange('cep', e.target.value)} className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500" />
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <p className="text-[10px] text-gray-400">Logradouro</p>
                      <p className="text-xs text-gray-700">{perfil.endereco.logradouro}, {perfil.endereco.numero}</p>
                      {perfil.endereco.complemento && <p className="text-xs text-gray-500">{perfil.endereco.complemento}</p>}
                    </div>
                    <div>
                      <p className="text-[10px] text-gray-400">Bairro</p>
                      <p className="text-xs text-gray-700">{perfil.endereco.bairro}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <p className="text-[10px] text-gray-400">Cidade</p>
                        <p className="text-xs text-gray-700">{perfil.endereco.cidade}</p>
                      </div>
                      <div>
                        <p className="text-[10px] text-gray-400">Estado</p>
                        <p className="text-xs text-gray-700">{perfil.endereco.estado}</p>
                      </div>
                    </div>
                    <div>
                      <p className="text-[10px] text-gray-400">CEP</p>
                      <p className="text-xs text-gray-700 font-mono">{perfil.endereco.cep}</p>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* ─── HISTORICO TAB ─── */}
            {tabAtiva === 'historico' && (
              <div className="space-y-3">
                {/* Resumo do Cliente */}
                <div className="border-t border-gray-100 pt-3 mt-2">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Resumo do Cliente</p>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="bg-gray-50 rounded-lg p-2.5 text-center">
                      <p className="text-xs font-bold text-gray-800">{tempoDesdeCadastro}</p>
                      <p className="text-[9px] text-gray-400">Meses na plataforma</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-2.5 text-center">
                      <p className="text-xs font-bold text-gray-800 capitalize">{perfil.tipo}</p>
                      <p className="text-[9px] text-gray-400">Tipo de conta</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-2.5 text-center">
                      <p className="text-xs font-bold text-gray-800">{perfil.dispositivosAtivos ?? 1}</p>
                      <p className="text-[9px] text-gray-400">Dispositivos ativos</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-2.5 text-center">
                      <p className="text-xs font-bold text-gray-800">{perfil.historicoTokens?.length ?? 0}</p>
                      <p className="text-[9px] text-gray-400">Tokens enviados</p>
                    </div>
                  </div>
                </div>

                {/* Cards de Resumo de Atividades */}
                {(() => {
                  const resumo = getResumoAtividades(perfil.email);
                  if (resumo.total === 0) return null;
                  return (
                    <div className="space-y-2">
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Últimas Atividades — Resumo</p>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="bg-blue-50 rounded-lg p-2.5 text-center">
                          <p className="text-xs font-bold text-blue-700">{resumo.logins}</p>
                          <p className="text-[9px] text-blue-400">Logins</p>
                        </div>
                        <div className="bg-emerald-50 rounded-lg p-2.5 text-center">
                          <p className="text-xs font-bold text-emerald-700">{resumo.transacoes}</p>
                          <p className="text-[9px] text-emerald-400">Transações</p>
                        </div>
                        <div className="bg-amber-50 rounded-lg p-2.5 text-center">
                          <p className="text-xs font-bold text-amber-700">{resumo.resgates}</p>
                          <p className="text-[9px] text-amber-400">Resgates</p>
                        </div>
                        <div className="bg-orange-50 rounded-lg p-2.5 text-center">
                          <p className="text-xs font-bold text-orange-700">{resumo.saques}</p>
                          <p className="text-[9px] text-orange-400">Saques</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <div className="bg-gray-50 rounded-lg p-2 text-center">
                          <p className="text-xs font-bold text-gray-800">{resumo.pontosAcumulados.toLocaleString('pt-BR')}</p>
                          <p className="text-[9px] text-gray-400">Pts. acumulados</p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-2 text-center">
                          <p className="text-xs font-bold text-gray-800">{resumo.pontosResgatados.toLocaleString('pt-BR')}</p>
                          <p className="text-[9px] text-gray-400">Pts. resgatados</p>
                        </div>
                        <div className="bg-red-50 rounded-lg p-2 text-center">
                          <p className="text-xs font-bold text-red-600">{resumo.falhasLogin}</p>
                          <p className="text-[9px] text-red-400">Falhas login</p>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* Timeline de Atividades */}
                {(() => {
                  const todasAtividades = getAtividadesPorEmail(perfil.email);
                  if (todasAtividades.length === 0) return (
                    <div className="text-center py-4">
                      <div className="w-10 h-10 rounded-full bg-gray-50 flex items-center justify-center mx-auto mb-2">
                        <div className="w-5 h-5 flex items-center justify-center">
                          <i className="ri-time-line text-gray-300 text-lg"></i>
                        </div>
                      </div>
                      <p className="text-xs text-gray-400">Nenhuma atividade registrada</p>
                    </div>
                  );

                  const atividadesFiltradas = filtroAtividade === 'todos'
                    ? todasAtividades
                    : todasAtividades.filter(a => a.tipo === filtroAtividade);

                  const tiposDisponiveis = Array.from(new Set(todasAtividades.map(a => a.tipo)));

                  return (
                    <div className="space-y-3">
                      {/* Filtros */}
                      <div className="flex items-center space-x-1 flex-wrap">
                        <button
                          onClick={() => setFiltroAtividade('todos')}
                          className={`px-2 py-1 rounded-md text-[10px] font-bold transition-all cursor-pointer whitespace-nowrap ${
                            filtroAtividade === 'todos'
                              ? 'bg-gray-800 text-white'
                              : 'bg-gray-100 text-gray-500 hover:text-gray-700'
                          }`}
                        >
                          Todos
                        </button>
                        {tiposDisponiveis.map(t => (
                          <button
                            key={t}
                            onClick={() => setFiltroAtividade(t)}
                            className={`px-2 py-1 rounded-md text-[10px] font-bold transition-all cursor-pointer whitespace-nowrap flex items-center space-x-1 ${
                              filtroAtividade === t
                                ? 'bg-gray-800 text-white'
                                : 'bg-gray-100 text-gray-500 hover:text-gray-700'
                            }`}
                          >
                            <div className="w-3 h-3 flex items-center justify-center">
                              <i className={`${getIconAtividade(t)} ${getColorAtividade(t)} text-[8px]`}></i>
                            </div>
                            <span>{getLabelAtividade(t)}</span>
                          </button>
                        ))}
                      </div>

                      {/* Lista */}
                      <div className="space-y-2">
                        {atividadesFiltradas.map((atv) => (
                          <div key={atv.id} className="flex items-start space-x-2.5 bg-gray-50 rounded-lg p-2.5">
                            <div className={`w-8 h-8 rounded-full ${getBgColorAtividade(atv.tipo)} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                              <div className="w-4 h-4 flex items-center justify-center">
                                <i className={`${getIconAtividade(atv.tipo)} ${getColorAtividade(atv.tipo)} text-xs`}></i>
                              </div>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center space-x-1.5 flex-wrap">
                                <p className="text-xs font-medium text-gray-800">{getLabelAtividade(atv.tipo)}</p>
                                {atv.status && (
                                  <span className={`inline-flex px-1.5 py-0.5 rounded text-[9px] font-bold ${getStatusColorAtividade(atv.status)}`}>
                                    {atv.status}
                                  </span>
                                )}
                              </div>
                              <p className="text-[10px] text-gray-500 mt-0.5">{atv.descricao}</p>
                              {/* Detalhes específicos por tipo */}
                              {atv.tipo === 'login' && atv.ip && (
                                <p className="text-[9px] text-gray-400 mt-0.5">
                                  {atv.ip} · {atv.dispositivo}
                                </p>
                              )}
                              {atv.tipo === 'transacao' && atv.pontos !== undefined && (
                                <p className="text-[9px] text-emerald-500 mt-0.5 font-medium">
                                  +{atv.pontos.toLocaleString('pt-BR')} pts · {atv.valor !== undefined && atv.valor > 0 ? `R$ ${atv.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : ''}
                                  {atv.campanhaNome && ` · ${atv.campanhaNome}`}
                                </p>
                              )}
                              {atv.tipo === 'resgate' && atv.pontos !== undefined && (
                                <p className="text-[9px] text-amber-500 mt-0.5 font-medium">
                                  -{atv.pontos.toLocaleString('pt-BR')} pts · R$ {(atv.valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                  {atv.campanhaNome && ` · ${atv.campanhaNome}`}
                                </p>
                              )}
                              {(atv.tipo === 'resgate' || atv.tipo === 'saque') && atv.detalhes?.destino && (
                                <p className="text-[9px] text-gray-400 mt-0.5">
                                  Destino: {atv.detalhes.destino} · Tipo: {atv.detalhes.tipo}
                                </p>
                              )}
                              {atv.tipo === 'alteracao_perfil' && atv.detalhes?.campo && (
                                <p className="text-[9px] text-gray-400 mt-0.5">
                                  Campo: {atv.detalhes.campo}
                                </p>
                              )}
                              <p className="text-[9px] text-gray-400 mt-1">{tempoRelativo(atv.data)} · {new Date(atv.data).toLocaleString('pt-BR')}</p>
                            </div>
                          </div>
                        ))}
                        {atividadesFiltradas.length === 0 && (
                          <div className="text-center py-3">
                            <p className="text-xs text-gray-400">Nenhuma atividade para este filtro</p>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}

                {/* Histórico de Tokens */}
                {perfil.historicoTokens && perfil.historicoTokens.length > 0 && (
                  <div className="border-t border-gray-100 pt-3">
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Histórico de Tokens</p>
                    <div className="space-y-2">
                      {perfil.historicoTokens.map((tk, idx) => (
                        <div key={idx} className="flex items-center justify-between bg-gray-50 rounded-lg p-2.5">
                          <div className="flex items-center space-x-2">
                            <div className="w-6 h-6 flex items-center justify-center">
                              <i className={`${
                                tk.canal === 'email' ? 'ri-mail-line text-amber-500' :
                                tk.canal === 'sms' ? 'ri-message-2-line text-blue-500' :
                                'ri-whatsapp-line text-emerald-500'
                              } text-xs`}></i>
                            </div>
                            <div>
                              <p className="text-[10px] font-medium text-gray-700 capitalize">{tk.tipo.replace('_', ' ')}</p>
                              <p className="text-[9px] text-gray-400">{tk.codigoMascarado} · {tempoRelativo(tk.enviadoEm)}</p>
                            </div>
                          </div>
                          <span className={`inline-flex px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${
                            tk.status === 'usado' ? 'bg-emerald-50 text-emerald-600' :
                            tk.status === 'pendente' ? 'bg-amber-50 text-amber-600' :
                            tk.status === 'expirado' ? 'bg-red-50 text-red-600' :
                            'bg-gray-50 text-gray-500'
                          }`}>
                            {tk.status}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ─── AÇÕES TAB ─── */}
            {tabAtiva === 'acoes' && (
              <div className="space-y-4">
                {/* Segurança */}
                <div>
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2 flex items-center space-x-1">
                    <div className="w-3 h-3 flex items-center justify-center">
                      <i className="ri-shield-keyhole-line text-gray-400 text-xs"></i>
                    </div>
                    <span>Segurança da Conta</span>
                  </p>
                  <div className="space-y-2">
                    {/* Reset de Senha */}
                    <div className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                      <div className="flex items-center space-x-2">
                        <div className="w-7 h-7 rounded-full bg-amber-100 flex items-center justify-center">
                          <div className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-key-2-line text-amber-600 text-xs"></i>
                          </div>
                        </div>
                        <div>
                          <p className="text-xs font-medium text-gray-700">Reset de Senha</p>
                          <p className="text-[9px] text-gray-400">Envia email com link de recadastro</p>
                        </div>
                      </div>
                      <button
                        onClick={handleResetSenha}
                        disabled={enviandoReset || perfil.tokenResetPendente}
                        className="px-3 py-1.5 bg-amber-600 hover:bg-amber-700 disabled:bg-gray-200 disabled:text-gray-400 text-white text-[10px] font-bold rounded-lg transition-all cursor-pointer whitespace-nowrap"
                      >
                        {enviandoReset ? (
                          <span className="flex items-center space-x-1">
                            <i className="ri-loader-4-line animate-spin"></i>
                            <span>Enviando...</span>
                          </span>
                        ) : perfil.tokenResetPendente ? (
                          'Pendente'
                        ) : (
                          'Enviar'
                        )}
                      </button>
                    </div>

                    {/* Cancelar token reset pendente */}
                    {perfil.tokenResetPendente && (
                      <div className="flex items-center justify-between bg-amber-50 rounded-lg p-3 border border-amber-100">
                        <div className="flex items-center space-x-2">
                          <div className="w-7 h-7 rounded-full bg-amber-100 flex items-center justify-center">
                            <div className="w-4 h-4 flex items-center justify-center">
                              <i className="ri-mail-send-line text-amber-600 text-xs"></i>
                            </div>
                          </div>
                          <div>
                            <p className="text-xs font-medium text-gray-700">Token de reset ativo</p>
                            <p className="text-[9px] text-gray-400">Via {perfil.tokenResetCanal} · enviado {tempoRelativo(perfil.tokenResetEnviadoEm || '')}</p>
                          </div>
                        </div>
                        <button
                          onClick={handleCancelarTokenReset}
                          className="px-3 py-1.5 bg-amber-100 hover:bg-amber-200 text-amber-700 text-[10px] font-bold rounded-lg transition-all cursor-pointer whitespace-nowrap"
                        >
                          Cancelar
                        </button>
                      </div>
                    )}

                    {/* Enviar Token */}
                    <div className="flex flex-col bg-gray-50 rounded-lg p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center">
                            <div className="w-4 h-4 flex items-center justify-center">
                              <i className="ri-shield-check-line text-blue-600 text-xs"></i>
                            </div>
                          </div>
                          <div>
                            <p className="text-xs font-medium text-gray-700">Enviar Token de Verificação</p>
                            <p className="text-[9px] text-gray-400">SMS, Email ou WhatsApp</p>
                          </div>
                        </div>
                        <button
                          onClick={handleEnviarToken}
                          disabled={enviandoToken}
                          className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-200 disabled:text-gray-400 text-white text-[10px] font-bold rounded-lg transition-all cursor-pointer whitespace-nowrap"
                        >
                          {enviandoToken ? (
                            <span className="flex items-center space-x-1">
                              <i className="ri-loader-4-line animate-spin"></i>
                              <span>Enviando...</span>
                            </span>
                          ) : (
                            'Enviar'
                          )}
                        </button>
                      </div>
                      <div className="flex items-center space-x-1">
                        {(['email', 'sms', 'whatsapp'] as const).map((c) => (
                          <button
                            key={c}
                            onClick={() => setCanalToken(c)}
                            className={`flex items-center space-x-1 px-2 py-1 rounded-md text-[10px] font-bold transition-all cursor-pointer ${
                              canalToken === c
                                ? 'bg-blue-600 text-white'
                                : 'bg-white text-gray-500 hover:text-gray-700 border border-gray-200'
                            }`}
                          >
                            <div className="w-3 h-3 flex items-center justify-center">
                              <i className={c === 'email' ? 'ri-mail-line' : c === 'sms' ? 'ri-message-2-line' : 'ri-whatsapp-line'}></i>
                            </div>
                            <span className="capitalize">{c === 'email' ? 'Email' : c === 'sms' ? 'SMS' : 'WhatsApp'}</span>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Bloquear/Desbloquear */}
                    <div className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                      <div className="flex items-center space-x-2">
                        <div className={`w-7 h-7 rounded-full ${perfil.status === 'bloqueado' ? 'bg-emerald-100' : 'bg-red-100'} flex items-center justify-center`}>
                          <div className="w-4 h-4 flex items-center justify-center">
                            <i className={`${perfil.status === 'bloqueado' ? 'ri-lock-unlock-line text-emerald-600' : 'ri-lock-line text-red-600'} text-xs`}></i>
                          </div>
                        </div>
                        <div>
                          <p className="text-xs font-medium text-gray-700">
                            {perfil.status === 'bloqueado' ? 'Desbloquear Conta' : 'Bloquear Conta'}
                          </p>
                          <p className="text-[9px] text-gray-400">
                            {perfil.status === 'bloqueado' ? 'Permitir login novamente' : 'Impedir acesso ao sistema'}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={handleBloquearConta}
                        className={`px-3 py-1.5 text-white text-[10px] font-bold rounded-lg transition-all cursor-pointer whitespace-nowrap ${
                          perfil.status === 'bloqueado'
                            ? 'bg-emerald-600 hover:bg-emerald-700'
                            : 'bg-red-600 hover:bg-red-700'
                        }`}
                      >
                        {perfil.status === 'bloqueado' ? 'Desbloquear' : 'Bloquear'}
                      </button>
                    </div>

                    {/* Toggle 2FA */}
                    <div className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                      <div className="flex items-center space-x-2">
                        <div className={`w-7 h-7 rounded-full ${perfil.mfaHabilitado ? 'bg-emerald-100' : 'bg-gray-100'} flex items-center justify-center`}>
                          <div className="w-4 h-4 flex items-center justify-center">
                            <i className={`${perfil.mfaHabilitado ? 'ri-shield-check-line text-emerald-600' : 'ri-smartphone-line text-gray-500'} text-xs`}></i>
                          </div>
                        </div>
                        <div>
                          <p className="text-xs font-medium text-gray-700">
                            {perfil.mfaHabilitado ? '2FA Habilitado' : 'Habilitar 2FA'}
                          </p>
                          <p className="text-[9px] text-gray-400">
                            {perfil.mfaHabilitado ? 'Autenticação em dois fatores ativa' : 'Adicionar camada extra de segurança'}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={handleToggleMFA}
                        className={`px-3 py-1.5 text-[10px] font-bold rounded-lg transition-all cursor-pointer whitespace-nowrap ${
                          perfil.mfaHabilitado
                            ? 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                            : 'bg-emerald-600 hover:bg-emerald-700 text-white'
                        }`}
                      >
                        {perfil.mfaHabilitado ? 'Desabilitar' : 'Habilitar'}
                      </button>
                    </div>

                    {/* Encerrar Sessões */}
                    <div className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                      <div className="flex items-center space-x-2">
                        <div className="w-7 h-7 rounded-full bg-orange-100 flex items-center justify-center">
                          <div className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-logout-box-line text-orange-600 text-xs"></i>
                          </div>
                        </div>
                        <div>
                          <p className="text-xs font-medium text-gray-700">Encerrar Todas as Sessões</p>
                          <p className="text-[9px] text-gray-400">{perfil.dispositivosAtivos ?? 0} dispositivo(s) ativo(s)</p>
                        </div>
                      </div>
                      <button
                        onClick={handleEncerrarSessoes}
                        disabled={(perfil.dispositivosAtivos ?? 0) === 0}
                        className="px-3 py-1.5 bg-orange-600 hover:bg-orange-700 disabled:bg-gray-200 disabled:text-gray-400 text-white text-[10px] font-bold rounded-lg transition-all cursor-pointer whitespace-nowrap"
                      >
                        Deslogar
                      </button>
                    </div>
                  </div>
                </div>

                {/* Verificação de Contato */}
                <div className="border-t border-gray-100 pt-3">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2 flex items-center space-x-1">
                    <div className="w-3 h-3 flex items-center justify-center">
                      <i className="ri-contacts-line text-gray-400 text-xs"></i>
                    </div>
                    <span>Verificação de Contato</span>
                  </p>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                      <div className="flex items-center space-x-2">
                        <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center">
                          <div className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-mail-line text-blue-600 text-xs"></i>
                          </div>
                        </div>
                        <div>
                          <p className="text-xs font-medium text-gray-700">Verificar Email</p>
                          <p className="text-[9px] text-gray-400">{perfil.email}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleVerificarContato('email')}
                        className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-[10px] font-bold rounded-lg transition-all cursor-pointer whitespace-nowrap"
                      >
                        Ping
                      </button>
                    </div>
                    <div className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                      <div className="flex items-center space-x-2">
                        <div className="w-7 h-7 rounded-full bg-emerald-100 flex items-center justify-center">
                          <div className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-smartphone-line text-emerald-600 text-xs"></i>
                          </div>
                        </div>
                        <div>
                          <p className="text-xs font-medium text-gray-700">Verificar Celular</p>
                          <p className="text-[9px] text-gray-400">{perfil.telefone}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleVerificarContato('telefone')}
                        className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-[10px] font-bold rounded-lg transition-all cursor-pointer whitespace-nowrap"
                      >
                        Ping
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal de Confirmação */}
      {confirmModal && (
        <>
          <div className="fixed inset-0 bg-black/40 z-40" onClick={() => setConfirmModal(null)}></div>
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl p-5 w-full max-w-sm shadow-2xl">
              <div className="flex items-center space-x-2 mb-3">
                <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-alert-line text-amber-600 text-sm"></i>
                  </div>
                </div>
                <h3 className="text-sm font-bold text-gray-800">{confirmModal.titulo}</h3>
              </div>
              <p className="text-xs text-gray-500 mb-4">{confirmModal.mensagem}</p>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setConfirmModal(null)}
                  className="flex-1 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-bold rounded-lg transition-all cursor-pointer"
                >
                  Cancelar
                </button>
                <button
                  onClick={confirmModal.acao}
                  className={`flex-1 py-2 text-white text-xs font-bold rounded-lg transition-all cursor-pointer ${
                    confirmModal.tipo === 'bloquear' ? 'bg-red-600 hover:bg-red-700' :
                    confirmModal.tipo === 'sessoes' ? 'bg-orange-600 hover:bg-orange-700' :
                    confirmModal.tipo === 'remover_campanha' ? 'bg-red-600 hover:bg-red-700' :
                    'bg-emerald-600 hover:bg-emerald-700'
                  }`}
                >
                  Confirmar
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Modal de Edição de Configuração da Campanha */}
      {showConfigModal && campanhaEditando && (
        <>
          <div className="fixed inset-0 bg-black/40 z-40" onClick={() => { setShowConfigModal(false); setCampanhaEditando(null); }}></div>
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl p-5 w-full max-w-sm shadow-2xl">
              <div className="flex items-center space-x-2 mb-4">
                <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-settings-3-line text-amber-600 text-sm"></i>
                  </div>
                </div>
                <div>
                  <h3 className="text-sm font-bold text-gray-800">Configuração Personalizada</h3>
                  <p className="text-[10px] text-gray-400">{campanhaEditando.nome} em {getCampanhaInfo(campanhaEditando.campanhaId)?.nome}</p>
                </div>
              </div>
              <div className="space-y-3">
                <div>
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Limite de Resgate Diário (R$)</label>
                  <input
                    type="number"
                    value={configEditData?.limiteResgateDiarioPersonalizado ?? ''}
                    onChange={(e) => setConfigEditData({ ...configEditData, limiteResgateDiarioPersonalizado: e.target.value ? Number(e.target.value) : undefined })}
                    placeholder="Padrão da campanha"
                    className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
                  />
                </div>
                <div>
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Limite de Resgate Mensal (R$)</label>
                  <input
                    type="number"
                    value={configEditData?.limiteResgateMensalPersonalizado ?? ''}
                    onChange={(e) => setConfigEditData({ ...configEditData, limiteResgateMensalPersonalizado: e.target.value ? Number(e.target.value) : undefined })}
                    placeholder="Padrão da campanha"
                    className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
                  />
                </div>
                <div>
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Taxa de Processamento PIX (%)</label>
                  <input
                    type="number"
                    step="0.1"
                    value={configEditData?.taxaProcessamentoPixPersonalizada ?? ''}
                    onChange={(e) => setConfigEditData({ ...configEditData, taxaProcessamentoPixPersonalizada: e.target.value ? Number(e.target.value) : undefined })}
                    placeholder="Padrão da campanha"
                    className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
                  />
                </div>
                <div>
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Prazo de Aprovação (horas)</label>
                  <input
                    type="number"
                    value={configEditData?.prazoAprovacaoSaquePersonalizado ?? ''}
                    onChange={(e) => setConfigEditData({ ...configEditData, prazoAprovacaoSaquePersonalizado: e.target.value ? Number(e.target.value) : undefined })}
                    placeholder="Padrão da campanha"
                    className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
                  />
                </div>
              </div>
              <div className="flex items-center space-x-2 mt-4">
                <button
                  onClick={() => { setShowConfigModal(false); setCampanhaEditando(null); }}
                  className="flex-1 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-bold rounded-lg transition-all cursor-pointer"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSalvarConfig}
                  className="flex-1 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-lg transition-all cursor-pointer"
                >
                  Salvar Config
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}